import * as Popover from '@radix-ui/react-popover';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { FieldShell, inputBase } from './Field';
import type { SelectOption } from './Select';

/**
 * Folds a string for matching: case, Latin accents ("Leoní" = "leoni") and the
 * Arabic spellings an editor types interchangeably — harakat and tatweel
 * dropped, every alef written as ا, ى as ي, ة as ه — so "جنسية" finds "الجنسيّة".
 */
export function foldForSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\p{M}ـ]/gu, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .toLocaleLowerCase()
    .trim();
}

function textOf(opt: SelectOption): string {
  return opt.textValue ?? (typeof opt.label === 'string' ? opt.label : opt.value);
}

/**
 * Every word of the query must appear in the option's label or its group
 * heading: "nat" finds Nationality, "consent photo" the photo consent.
 */
export function filterOptions(
  options: readonly SelectOption[],
  query: string,
  pinned: ReadonlySet<string>,
): SelectOption[] {
  const words = foldForSearch(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...options];
  return options.filter((opt) => {
    if (pinned.has(opt.value)) return true;
    const hay = foldForSearch(`${textOf(opt)} ${opt.group ?? ''}`);
    return words.every((w) => hay.includes(w));
  });
}

/**
 * A select whose open list starts with a search field — for the lists too long
 * to scroll (the import wizard's "Import as" holds every student, enrolment,
 * consent, survey and custom-field target). Same props as `Select`, so a call
 * site swaps one for the other; options group the same way (consecutive options
 * sharing `group`), and the groups survive filtering.
 *
 * Keyboard: the trigger opens on Enter, Space, the arrows or by typing (the
 * typed character starts the search); in the list, type to filter, arrows /
 * Home / End to move, Enter to choose, Esc to close — focus returns to the
 * trigger either way.
 */
export function SearchableSelect({
  label,
  value,
  onValueChange,
  options,
  placeholder,
  hint,
  required,
  error,
  disabled,
  className,
  pinnedValues,
}: {
  label?: ReactNode;
  value: string | undefined;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  hint?: ReactNode;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  className?: string;
  /** Options listed whatever the search says (the wizard's "Ignore this column"). */
  pinnedValues?: readonly string[];
}) {
  const { t } = useTranslation();
  const id = useId();
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);

  const pinned = useMemo(() => new Set(pinnedValues ?? []), [pinnedValues]);
  const visible = useMemo(() => filterOptions(options, query, pinned), [options, query, pinned]);
  const selected = options.find((o) => o.value === value);
  const searching = foldForSearch(query) !== '';
  const nothingMatches = searching && visible.every((o) => pinned.has(o.value));

  const firstEnabled = (list: SelectOption[], from = 0, step = 1) => {
    for (let i = from; i >= 0 && i < list.length; i += step) if (!list[i].disabled) return i;
    return -1;
  };
  // While searching, the first real match is the one Enter takes — not the
  // pinned "Ignore" above it.
  const firstMatch = (list: SelectOption[]) => {
    const i = list.findIndex((o) => !o.disabled && !pinned.has(o.value));
    return i >= 0 ? i : firstEnabled(list);
  };

  const openWith = (initial: string) => {
    setQuery(initial);
    const list = filterOptions(options, initial, pinned);
    const current = list.findIndex((o) => o.value === value && !o.disabled);
    setActive(initial ? firstMatch(list) : current >= 0 ? current : firstEnabled(list));
    setOpen(true);
  };

  const choose = (opt: SelectOption | undefined) => {
    if (!opt || opt.disabled) return;
    onValueChange(opt.value);
    setOpen(false);
  };

  useEffect(() => {
    if (!open || active < 0) return;
    // jsdom has no scrollIntoView.
    document.getElementById(`${id}-opt-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, id]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (open || disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openWith('');
    } else if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      openWith(e.key);
    }
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const move = (to: number) => {
      e.preventDefault();
      if (to >= 0) setActive(to);
    };
    if (e.key === 'ArrowDown') move(firstEnabled(visible, active + 1));
    else if (e.key === 'ArrowUp') move(firstEnabled(visible, Math.max(active - 1, 0), -1));
    else if (e.key === 'Home') move(firstEnabled(visible));
    else if (e.key === 'End') move(firstEnabled(visible, visible.length - 1, -1));
    else if (e.key === 'Enter') {
      e.preventDefault();
      choose(visible[active]);
    } else if (e.key === 'Tab') setOpen(false);
  };

  const renderOption = (opt: SelectOption, i: number) => {
    const isCurrent = opt.value === value;
    return (
      <div
        key={opt.value}
        id={optionId(i)}
        role="option"
        aria-selected={i === active}
        aria-disabled={opt.disabled || undefined}
        aria-current={isCurrent || undefined}
        // mousedown would move focus off the search field and lose its caret.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(opt)}
        onMouseMove={() => !opt.disabled && active !== i && setActive(i)}
        className={cn(
          'flex cursor-pointer items-start gap-xs px-sm py-xs text-small text-ink-graphite',
          i === active && 'bg-canvas-sand',
          isCurrent && 'text-accent-blue',
          opt.disabled && 'cursor-not-allowed text-ink-mute',
        )}
      >
        <span aria-hidden className="w-[1ch] shrink-0">
          {isCurrent ? '✓' : ''}
        </span>
        <span className="flex flex-col">
          <span>{opt.label}</span>
          {opt.description && <span className="max-w-prose text-small text-ink-mute">{opt.description}</span>}
        </span>
      </div>
    );
  };

  // Consecutive options under one heading, keeping each option's index in `visible`.
  const blocks: { group?: string; items: { opt: SelectOption; i: number }[] }[] = [];
  visible.forEach((opt, i) => {
    const last = blocks[blocks.length - 1];
    if (last && last.group === opt.group) last.items.push({ opt, i });
    else blocks.push({ group: opt.group, items: [{ opt, i }] });
  });

  return (
    <FieldShell label={label} htmlFor={id} required={required} error={error} hint={hint}>
      <Popover.Root
        open={open}
        onOpenChange={(next) => (next ? openWith('') : setOpen(false))}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-labelledby={label ? `${labelId} ${valueId}` : valueId}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onKeyDown={onTriggerKeyDown}
            className={cn(
              inputBase,
              'flex items-center justify-between gap-sm text-start',
              error && 'border-signal-error',
              className,
            )}
          >
            <span id={valueId} className={cn('truncate', !selected && 'text-ink-mute')}>
              {selected ? selected.label : (placeholder ?? t('ui.selectPlaceholder'))}
            </span>
            <span aria-hidden className="text-ink-mute">
              ▾
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            aria-labelledby={label ? labelId : undefined}
            aria-label={label ? undefined : t('ui.searchOptions')}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            className="z-95 flex max-h-[min(420px,var(--radix-popover-content-available-height))] w-max min-w-(--radix-popover-trigger-width) max-w-[min(440px,var(--radix-popover-content-available-width))] flex-col rounded-sm border-[0.5px] border-hairline bg-canvas-paper shadow-nav-scrolled"
          >
            <div className="border-b-[0.5px] border-hairline p-xs">
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label={t('ui.searchOptions')}
                aria-expanded
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={active >= 0 && visible[active] ? optionId(active) : undefined}
                autoComplete="off"
                spellCheck={false}
                dir="auto"
                value={query}
                placeholder={t('ui.searchPlaceholder')}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(firstMatch(filterOptions(options, e.target.value, pinned)));
                }}
                onKeyDown={onInputKeyDown}
                className={cn(inputBase, 'py-xs')}
              />
            </div>
            <div
              id={listId}
              role="listbox"
              aria-labelledby={label ? labelId : undefined}
              aria-label={label ? undefined : t('ui.searchOptions')}
              className="min-h-0 flex-1 overflow-auto py-2xs"
            >
              {blocks.map((b, bi) =>
                b.group ? (
                  <div key={`${bi}-${b.group}`} role="presentation">
                    <div
                      role="presentation"
                      id={`${id}-group-${bi}`}
                      className="px-sm pt-xs pb-2xs text-small font-medium text-ink-mute"
                    >
                      {b.group}
                    </div>
                    <div role="group" aria-labelledby={`${id}-group-${bi}`}>
                      {b.items.map(({ opt, i }) => renderOption(opt, i))}
                    </div>
                  </div>
                ) : (
                  b.items.map(({ opt, i }) => renderOption(opt, i))
                ),
              )}
            </div>
            <p role="status" className={cn('px-sm text-small text-ink-mute', nothingMatches ? 'py-xs' : 'sr-only')}>
              {nothingMatches ? t('ui.noMatch', { query: query.trim() }) : ''}
            </p>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </FieldShell>
  );
}
